/**
 * ControlPoints - Interactive 3D boundary control points
 * 
 * Renders draggable spheres at boundary curve positions.
 * Supports raycaster-based drag to reposition points with surface snapping.
 */

import React, { useRef, useState, useMemo, useCallback } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'

interface ControlPointsProps {
  /** Control point positions [[x,y,z], ...] */
  points: number[][]
  /** Callback when points change */
  onPointsChange: (newPoints: number[][]) => void
  /** Optional foot mesh for surface snapping */
  footMesh?: THREE.Mesh | null
  /** Point radius in model units (default: 1.0mm) */
  pointRadius?: number
  /** Normal color (default: orange) */
  normalColor?: string
  /** Hover/selected color (default: cyan) */
  hoverColor?: string
  /** Whether smooth region selection mode is active */
  isSmoothMode?: boolean
  /** Callback when a control point is clicked in smooth mode */
  onSmoothRegionSelect?: (index: number) => void
  /** Smooth region start index */
  smoothStartIndex?: number
  /** Smooth region end index */
  smoothEndIndex?: number
}

/**
 * Single control point sphere
 */
interface ControlPointProps {
  position: [number, number, number]
  index: number
  isHovered: boolean
  isSelected: boolean
  radius: number
  normalColor: string
  hoverColor: string
  onPointerDown: (index: number, event: any) => void
  onPointerEnter: (index: number) => void
  onPointerLeave: () => void
}

const ControlPoint: React.FC<ControlPointProps> = ({
  position,
  index,
  isHovered,
  isSelected,
  radius,
  normalColor,
  hoverColor,
  onPointerDown,
  onPointerEnter,
  onPointerLeave
}) => {
  const color = isHovered || isSelected ? hoverColor : normalColor

  return (
    <mesh
      position={position}
      onPointerDown={(e) => {
        e.stopPropagation()
        onPointerDown(index, e)
      }}
      onPointerEnter={(e) => {
        e.stopPropagation()
        onPointerEnter(index)
      }}
      onPointerLeave={(e) => {
        e.stopPropagation()
        onPointerLeave()
      }}
    >
      <sphereGeometry args={[radius, 16, 16]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={isHovered || isSelected ? 0.3 : 0.1}
      />
    </mesh>
  )
}

/**
 * Generate control points at ~5mm spacing along a curve
 */
function generateControlPoints(boundaryCurve: number[][], targetSpacing: number = 5.0): number[][] {
  if (boundaryCurve.length < 2) return boundaryCurve

  const points: number[][] = []
  let accumulatedDistance = 0

  points.push(boundaryCurve[0])

  for (let i = 1; i < boundaryCurve.length; i++) {
    const p1 = boundaryCurve[i - 1]
    const p2 = boundaryCurve[i]
    const distance = Math.sqrt(
      Math.pow(p2[0] - p1[0], 2) +
      Math.pow(p2[1] - p1[1], 2) +
      Math.pow(p2[2] - p1[2], 2)
    )

    accumulatedDistance += distance

    if (accumulatedDistance >= targetSpacing) {
      points.push(p2)
      accumulatedDistance = 0
    }
  }

  // Always include the last point
  if (points.length === 0 || points[points.length - 1] !== boundaryCurve[boundaryCurve.length - 1]) {
    points.push(boundaryCurve[boundaryCurve.length - 1])
  }

  return points
}

/**
 * Interpolate smoothly between control points using Catmull-Rom spline
 */
function interpolateControlPoints(controlPoints: number[][], samplesPerSegment: number = 10): number[][] {
  if (controlPoints.length < 2) return controlPoints

  const vectors = controlPoints.map(([x, y, z]) => new THREE.Vector3(x, y, z))
  const curve = new THREE.CatmullRomCurve3(vectors, true) // true = closed curve

  const totalSamples = controlPoints.length * samplesPerSegment
  const interpolatedPoints: number[][] = []

  for (let i = 0; i <= totalSamples; i++) {
    const t = i / totalSamples
    const point = curve.getPoint(t)
    interpolatedPoints.push([point.x, point.y, point.z])
  }

  return interpolatedPoints
}

/**
 * Project a point onto a mesh surface using raycasting
 */
function projectPointToSurface(
  point: THREE.Vector3,
  mesh: THREE.Mesh,
  raycaster: THREE.Raycaster
): THREE.Vector3 {
  // Try raycasting in multiple directions to find the closest surface
  const directions = [
    new THREE.Vector3(0, 0, -1), // Down
    new THREE.Vector3(0, 0, 1),  // Up
    new THREE.Vector3(0, -1, 0), // Plantar normal (approx)
    new THREE.Vector3(0, 1, 0),  // Dorsal normal
  ]

  let closestIntersection: THREE.Intersection | null = null
  let minDistance = Infinity

  for (const direction of directions) {
    raycaster.set(point, direction)
    const intersects = raycaster.intersectObject(mesh, false)

    if (intersects.length > 0) {
      const intersection = intersects[0]
      const distance = point.distanceTo(intersection.point)

      if (distance < minDistance) {
        minDistance = distance
        closestIntersection = intersection
      }
    }
  }

  if (closestIntersection) {
    return closestIntersection.point.clone()
  }

  // If no intersection found, return original point
  return point.clone()
}

/**
 * Main ControlPoints component
 */
export const ControlPoints: React.FC<ControlPointsProps> = ({
  points,
  onPointsChange,
  footMesh = null,
  pointRadius = 1.0,
  normalColor = '#FF9800',
  hoverColor = '#00CED1',
  isSmoothMode = false,
  onSmoothRegionSelect,
  smoothStartIndex = -1,
  smoothEndIndex = -1
}) => {
  const { camera, gl } = useThree()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const raycasterRef = useRef(new THREE.Raycaster())
  const planeRef = useRef(new THREE.Plane())
  const intersectionPointRef = useRef(new THREE.Vector3())

  // Generate control points from boundary curve
  const controlPoints = useMemo(() => {
    return generateControlPoints(points, 5.0)
  }, [points])

  const handlePointerDown = useCallback((index: number, event: any) => {
    // In smooth mode, clicking selects region endpoints instead of dragging
    if (isSmoothMode && onSmoothRegionSelect) {
      onSmoothRegionSelect(index)
      event.stopPropagation()
      return
    }

    setSelectedIndex(index)
    setIsDragging(true)

    // Create a plane perpendicular to the camera at the point's position
    const point = new THREE.Vector3(...controlPoints[index] as [number, number, number])
    const normal = new THREE.Vector3()
    normal.subVectors(camera.position, point).normalize()
    planeRef.current.setFromNormalAndCoplanarPoint(normal, point)

    event.stopPropagation()
  }, [controlPoints, camera, isSmoothMode, onSmoothRegionSelect])

  const handlePointerMove = useCallback((event: MouseEvent) => {
    if (!isDragging || selectedIndex === null) return

    // Calculate mouse position in normalized device coordinates
    const rect = gl.domElement.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1

    // Update raycaster from camera and mouse position
    const mouse = new THREE.Vector2(x, y)
    raycasterRef.current.setFromCamera(mouse, camera)

    // Find intersection with plane
    raycasterRef.current.ray.intersectPlane(planeRef.current, intersectionPointRef.current)

    if (intersectionPointRef.current) {
      let newPosition = intersectionPointRef.current.clone()

      // Project to surface if foot mesh is available
      if (footMesh) {
        newPosition = projectPointToSurface(newPosition, footMesh, raycasterRef.current)
      }

      // Update control points
      const newControlPoints = [...controlPoints]
      newControlPoints[selectedIndex] = [newPosition.x, newPosition.y, newPosition.z]

      // Interpolate to get full boundary curve
      const interpolatedBoundary = interpolateControlPoints(newControlPoints, 10)
      onPointsChange(interpolatedBoundary)
    }
  }, [isDragging, selectedIndex, controlPoints, footMesh, camera, gl, onPointsChange])

  const handlePointerUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Attach global mouse event listeners during drag
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handlePointerMove)
      window.addEventListener('mouseup', handlePointerUp)

      return () => {
        window.removeEventListener('mousemove', handlePointerMove)
        window.removeEventListener('mouseup', handlePointerUp)
      }
    }
  }, [isDragging, handlePointerMove, handlePointerUp])

  const handlePointerEnter = useCallback((index: number) => {
    setHoveredIndex(index)
  }, [])

  const handlePointerLeave = useCallback(() => {
    setHoveredIndex(null)
  }, [])

  // Determine if a point is inside the smooth region
  const isInSmoothRegion = useCallback((index: number): boolean => {
    if (!isSmoothMode || smoothStartIndex == null) return false
    if (smoothEndIndex == null) return index === smoothStartIndex
    const lo = Math.min(smoothStartIndex, smoothEndIndex)
    const hi = Math.max(smoothStartIndex, smoothEndIndex)
    return index >= lo && index <= hi
  }, [isSmoothMode, smoothStartIndex, smoothEndIndex])

  const SMOOTH_REGION_COLOR = '#10b981'

  return (
    <group>
      {controlPoints.map((point, index) => (
        <ControlPoint
          key={index}
          position={point as [number, number, number]}
          index={index}
          isHovered={hoveredIndex === index}
          isSelected={selectedIndex === index}
          radius={pointRadius}
          normalColor={isInSmoothRegion(index) ? SMOOTH_REGION_COLOR : normalColor}
          hoverColor={hoverColor}
          onPointerDown={handlePointerDown}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        />
      ))}
    </group>
  )
}
